// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  LOADTO,
  LOACreateRequest,
  LOASendForSigningRequest,
  EmbeddedSigningUrlResponse
} from '../types/loa';
import type { PageResponse } from '../types/relationship';

export const loaApi = {
  /**
   * Get all LOAs for a profile with pagination
   */
  getLOAsByProfile: async (profileId: string, page: number = 0, size: number = 20): Promise<PageResponse<LOADTO>> => {
    const response = await api.get<PageResponse<LOADTO>>(`/loa/profile/${profileId}`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * Get single LOA by ID
   */
  getLOA: async (id: string): Promise<LOADTO> => {
    const response = await api.get<LOADTO>(`/loa/${id}`);
    return response.data;
  },

  /**
   * Create new LOA
   */
  createLOA: async (data: LOACreateRequest): Promise<LOADTO> => {
    const response = await api.post<LOADTO>('/loa', data);
    return response.data;
  },

  /**
   * Send LOA for DocuSign signing
   */
  sendForSigning: async (id: string, data: LOASendForSigningRequest): Promise<LOADTO> => {
    const response = await api.post<LOADTO>(`/loa/${id}/send-for-signing`, data);
    return response.data;
  },

  /**
   * Get embedded signing URL for a signatory
   */
  getEmbeddedSigningUrl: async (
    loaId: string,
    clientId: string,
    returnUrl?: string
  ): Promise<EmbeddedSigningUrlResponse> => {
    const response = await api.get<EmbeddedSigningUrlResponse>(`/loa/${loaId}/embedded-signing-url`, {
      params: { clientId, returnUrl }
    });
    return response.data;
  },

  /**
   * Download signed LOA document
   */
  downloadSignedDocument: async (id: string): Promise<Blob> => {
    const response = await api.get(`/loa/${id}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Void/cancel an LOA
   */
  voidLOA: async (id: string, reason: string): Promise<LOADTO> => {
    const response = await api.post<LOADTO>(`/loa/${id}/void`, { reason });
    return response.data;
  }
};
